/**
 * HipoWorklist — the year's published high-potentials with their plans and
 * the Good Governance People Forum's last review of each. The HR process owner (7 Sep 2026):
 * the review identifies; the forum then manages the development plans every
 * six to eight weeks. HR marks a person reviewed with a note.
 */
import { useState } from 'react'
import toast from 'react-hot-toast'
import { CheckCircle2, ClipboardList } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { useHipoWorklist, useRecordForumReview } from '@/hooks/useTalentReview'
import type { HipoWorklistItem } from '@/services/api/talentReviewService'
import { MIDYEAR_RATING_LABEL, READINESS_LABEL, type Readiness } from './eduvosBlocks'

const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('en-ZA', { dateStyle: 'medium' }) : null)

/** Six to eight weeks is the forum's rhythm; past eight weeks the row asks for attention. */
const OVERDUE_DAYS = 56

const overdue = (item: HipoWorklistItem) => {
  if (!item.forum_reviewed_at) return true
  return (Date.now() - new Date(item.forum_reviewed_at).getTime()) / 86400000 > OVERDUE_DAYS
}

export default function HipoWorklist({ reviewYear, className }: { reviewYear: number; className?: string }) {
  const { data, isLoading, error } = useHipoWorklist(reviewYear)
  const { mutate: record, isPending } = useRecordForumReview()
  const [noteFor, setNoteFor] = useState<string | null>(null)
  const [note, setNote] = useState('')

  const items = data?.items || []
  const due = items.filter(overdue).length

  const submit = (item: HipoWorklistItem) => {
    record(
      { userId: item.employee_id, reviewYear, note: note.trim() || undefined },
      {
        onSuccess: () => {
          toast.success(`${item.name || item.employee_id} marked as reviewed.`)
          setNoteFor(null)
          setNote('')
        },
        onError: (err) => toast.error(err.message || 'Could not record the review'),
      },
    )
  }

  return (
    <Card className={className} data-demo="talent-hipo-worklist">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-heritage-blue" />
            <CardTitle className="text-base">High-potentials {reviewYear}</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">{items.length} published</Badge>
            {due > 0 && (
              <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800">
                {due} due for review
              </Badge>
            )}
          </div>
        </div>
        <CardDescription>
          What the Annual I&G Review published, with each person's growth plan and when it was last reviewed.
          Due means not reviewed in the last eight weeks.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {error && <p className="text-sm text-red-700">{(error as Error).message || 'Could not load the worklist.'}</p>}
        {!isLoading && !error && items.length === 0 && (
          <p className="text-sm text-muted-foreground">No high-potentials have been published for {reviewYear} yet.</p>
        )}
        {items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 pr-3">Person</th>
                  <th className="py-2 pr-3">People Leader</th>
                  <th className="py-2 pr-3">Block</th>
                  <th className="py-2 pr-3">Next role</th>
                  <th className="py-2 pr-3">Growth plan</th>
                  <th className="py-2 pr-3">Last reviewed</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.employee_id} className={cn('border-b align-top', overdue(item) && 'bg-amber-50/40')} data-demo="talent-hipo-row">
                    <td className="py-2 pr-3">
                      <p className="font-medium">{item.name || item.employee_id}</p>
                      <p className="text-xs text-muted-foreground">{[item.job_title, item.department].filter(Boolean).join(' · ')}</p>
                      {item.midyear_rating && (
                        <p className="text-xs text-muted-foreground">Mid-year: {MIDYEAR_RATING_LABEL[item.midyear_rating] || item.midyear_rating}</p>
                      )}
                    </td>
                    <td className="py-2 pr-3">{item.leader_name || '—'}</td>
                    <td className="py-2 pr-3">{item.block ? <Badge variant="outline">Block {item.block}</Badge> : '—'}</td>
                    <td className="py-2 pr-3">
                      <p>{item.next_role || '—'}</p>
                      {item.readiness && <p className="text-xs text-muted-foreground">{READINESS_LABEL[item.readiness as Readiness] || item.readiness}</p>}
                    </td>
                    <td className="py-2 pr-3 max-w-xs">
                      {item.idp ? (
                        <>
                          {item.idp.focus && <p className="text-xs font-medium">{item.idp.focus}</p>}
                          {item.idp.areas.length > 0 && <p className="text-xs text-muted-foreground">{item.idp.areas.join(' · ')}</p>}
                          {item.proposed_items > 0 && (
                            <p className="text-xs text-amber-800">{item.proposed_items} proposed item{item.proposed_items === 1 ? '' : 's'} waiting</p>
                          )}
                        </>
                      ) : (
                        <p className="text-xs text-muted-foreground">No growth plan for {reviewYear}</p>
                      )}
                      {item.development_plan && (
                        <details className="mt-1 text-xs text-muted-foreground">
                          <summary className="cursor-pointer select-none">What the review agreed</summary>
                          <p className="mt-1 whitespace-pre-line">{item.development_plan}</p>
                        </details>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      {item.forum_reviewed_at ? (
                        <>
                          <p className="flex items-center gap-1 text-xs">
                            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> {fmt(item.forum_reviewed_at)}
                          </p>
                          {item.forum_review_note && <p className="text-xs text-muted-foreground">{item.forum_review_note}</p>}
                        </>
                      ) : (
                        <p className="text-xs text-muted-foreground">Not yet reviewed</p>
                      )}
                    </td>
                    <td className="py-2">
                      {noteFor === item.employee_id ? (
                        <div className="flex flex-col gap-1.5">
                          <Input
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Note for the record (optional)"
                            className="h-8 w-56 text-xs"
                            aria-label={`Review note for ${item.name || item.employee_id}`}
                          />
                          <div className="flex gap-1.5">
                            <Button size="sm" className="h-7 text-xs" disabled={isPending} onClick={() => submit(item)}>
                              Mark reviewed today
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setNoteFor(null)}>
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setNoteFor(item.employee_id); setNote('') }}>
                          Reviewed…
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
